import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { replaceTokensInUrl } from '@cat-ecom/common/utils/stringUtils';
import {
  LEGACY_MY_ACCOUNT_PAGES,
  LEGACY_ORDER_DETAILS_ROUTE,
  LEGACY_PENDING_ORDER_DETAILS_ROUTE,
  LEGACY_PENDING_QUOTE_DETAIL_ROUTE,
  LEGACY_ORDER_STATUS_ROUTE,
  SAVED_LIST_DETAIL_URL,
  HEADLESS_MY_ACCOUNT_LINKS,
  LEGACY_ACCOUNT_SECTION_ROUTE,
  SHOPPING_CART_URL_PARAMETER
} from 'constants/links';
import { MY_ACCOUNT_SEARCH } from '../utils/constants';
import { useParams } from 'next/navigation';
import { normalizeCommerceLink } from 'utils/links';
import { getRelativeURLWithParams } from 'utils/url';

const useMyAccountLinks = (catalogId, storeId, langId) => {
  const t = useTranslations();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const { lang, esite } = useParams();

  const dynamicLinks = useMemo(
    () => ({
      ORDER_DETAIL: {
        route: LEGACY_MY_ACCOUNT_PAGES.ORDER_DETAILS,
        getLabel: (orderNumber) =>
          `${t('2413_ORDER_SHIPMENT_ORDER_NUMBER')} ${orderNumber}`,
        getUrl: ({ dbsOrderId, orderId }) =>
          replaceTokensInUrl(
            LEGACY_ORDER_DETAILS_ROUTE,
            storeId,
            langId,
            orderId,
            dbsOrderId
          )
      },
      PENDING_QUOTE_DETAIL: {
        route: LEGACY_MY_ACCOUNT_PAGES.PENDING_QUOTE_DETAILS,
        getUrl: ({ orderId = '' }) =>
          replaceTokensInUrl(
            LEGACY_PENDING_QUOTE_DETAIL_ROUTE,
            catalogId,
            langId,
            storeId,
            orderId
          )
      },
      PENDING_ORDER_DETAILS: {
        route: LEGACY_MY_ACCOUNT_PAGES.PENDING_ORDER_DETAILS,
        getUrl: ({ orderId = '' }) =>
          replaceTokensInUrl(
            LEGACY_PENDING_ORDER_DETAILS_ROUTE,
            orderId,
            storeId,
            langId,
            catalogId
          )
      },
      ORDER_STATUS: {
        route: LEGACY_MY_ACCOUNT_PAGES.ORDER_STATUS,
        getUrl: ({ orderId = '', dbsOrderId = '' }) =>
          replaceTokensInUrl(
            LEGACY_ORDER_STATUS_ROUTE,
            catalogId,
            langId,
            orderId,
            storeId,
            dbsOrderId
          )
      },
      SAVED_LIST_DETAIL: {
        route: MY_ACCOUNT_SEARCH.LIST_DETAILS,
        getUrl: ({
          createdBy = '',
          name = '',
          orderId = ''
        }: {
          createdBy: string;
          name: string;
          orderId: string | number;
        }) =>
          replaceTokensInUrl(
            SAVED_LIST_DETAIL_URL,
            catalogId,
            langId,
            storeId,
            createdBy,
            orderId,
            name
          )
      },
      SHOPPING_CART: {
        getUrl: ({ orderId = '' }: { orderId: string | number }) =>
          normalizeCommerceLink(
            getRelativeURLWithParams(SHOPPING_CART_URL_PARAMETER, {
              storeId,
              orderId
            })
          )
      }
    }),
    [t, catalogId, langId, storeId]
  );

  const staticLinks = useMemo(
    () => ({
      PERSONAL_INFO: {
        route: LEGACY_MY_ACCOUNT_PAGES.PERSONAL_INFORMATION,
        getUrl: () =>
          normalizeCommerceLink(
            getRelativeURLWithParams(LEGACY_ACCOUNT_SECTION_ROUTE, {
              route: LEGACY_MY_ACCOUNT_PAGES.PERSONAL_INFORMATION,
              storeId,
              langId,
              catalogId
            })
          )
      },
      ADDRESS_BOOK: {
        route: HEADLESS_MY_ACCOUNT_LINKS.ADDRESS_BOOK,
        getUrl: () =>
          `/${lang}/${esite}${HEADLESS_MY_ACCOUNT_LINKS.ADDRESS_BOOK}`
      },
      PAYMENT_METHODS: {
        route: LEGACY_MY_ACCOUNT_PAGES.PAYMENT,
        getUrl: () =>
          normalizeCommerceLink(
            getRelativeURLWithParams(LEGACY_ACCOUNT_SECTION_ROUTE, {
              route: LEGACY_MY_ACCOUNT_PAGES.PAYMENT,
              storeId,
              langId,
              catalogId
            })
          )
      },
      SHOPPING_PREFERENCES: {
        route: LEGACY_MY_ACCOUNT_PAGES.PREFERENCES,
        getUrl: () =>
          normalizeCommerceLink(
            getRelativeURLWithParams(LEGACY_ACCOUNT_SECTION_ROUTE, {
              route: LEGACY_MY_ACCOUNT_PAGES.PREFERENCES,
              storeId,
              langId,
              catalogId
            })
          )
      },
      MY_EQUIPMENT_UPDATE: {
        route: LEGACY_MY_ACCOUNT_PAGES.EQUIPMENT,
        getUrl: () =>
          normalizeCommerceLink(
            getRelativeURLWithParams(LEGACY_ACCOUNT_SECTION_ROUTE, {
              route: LEGACY_MY_ACCOUNT_PAGES.EQUIPMENT,
              storeId,
              langId,
              catalogId
            })
          )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, catalogId, langId, storeId]
  );

  return { dynamicLinks, staticLinks };
};

export default useMyAccountLinks;
